/**
 * 文档管理器
 * 提供文档处理、存储和管理的核心业务逻辑
 *
 * 核心功能：
 * 1. 文档处理与分析
 * 2. 文档存储和检索
 * 3. 文档状态管理
 * 4. 文档删除
 *
 * 该类整合了ContentProcessorService和TextDocumentStorageService的功能，
 * 提供统一的文档管理接口，不包含UI相关代码。
 */
import { ContentType, } from '@readsavor/types';
import { ContentProcessorService } from '../../content/services/content-processor.service.js';
// 预期此导入将通过工厂或服务注入获得，这里直接从utils中导入
import { TextDocumentStorageProvider } from '@readsavor/utils';
/**
 * 文档管理器类
 * 负责文档的处理、存储和管理
 */
export class DocumentManager {
    /**
     * 私有构造函数，实现单例模式
     */
    constructor() {
        this.contentProcessor = ContentProcessorService.getInstance();
        this.textDocumentStorage = TextDocumentStorageProvider.getInstance();
    }
    /**
     * 获取DocumentManager实例
     * @returns DocumentManager单例实例
     */
    static getInstance() {
        if (!DocumentManager.instance) {
            DocumentManager.instance = new DocumentManager();
        }
        return DocumentManager.instance;
    }
    /**
     * 处理文本内容
     * 对文本进行分析和分段处理
     * @param text 原始文本内容
     * @returns 处理后的段落数据
     */
    async processContent(text) {
        try {
            const processedData = await this.contentProcessor.processContent(ContentType.TEXT, text);
            if (!processedData.metadata?.paragraphs) {
                return { paragraphs: [] };
            }
            return { paragraphs: processedData.metadata.paragraphs };
        }
        catch (error) {
            console.error('Error processing content:', error);
            throw new Error('Failed to process content');
        }
    }
    /**
     * 存储文档
     * 将文本保存到本地存储
     * @param text 原始文本内容
     * @param options 文档选项（语言、目标语言等）
     * @returns 新存储的文档ID
     */
    async storeDocument(text, options) {
        try {
            const paragraphs = options.paragraphs || (await this.processContent(text)).paragraphs;
            const documentToStore = {
                contentType: 'text',
                title: options.title || '',
                language: options.language,
                targetLanguage: options.targetLanguage,
                timestamp: Date.now(),
                preview: text.substring(0, 200),
                size: text.length,
                totalParagraphs: paragraphs.length,
                lastAccessTime: Date.now(),
            };
            const documentId = await this.textDocumentStorage.addDocument(documentToStore);
            // 处理段落存储
            const paragraphsToAdd = paragraphs.map((p, index) => ({
                textId: documentId.id,
                paragraphId: p.id,
                index: index,
                content: p.content,
            }));
            await this.textDocumentStorage.addParagraphs(paragraphsToAdd);
            return documentId;
        }
        catch (error) {
            console.error('Failed to store document:', error);
            throw error;
        }
    }
    /**
     * 加载文档
     * 从存储中读取文档及其段落
     * @param identity 文档标识
     * @returns 加载的文档数据
     */
    async loadDocument(identity) {
        try {
            const document = await this.textDocumentStorage.getDocument(identity);
            if (!document) {
                console.warn('[DocumentManager] Document not found in storage for identity:', identity);
                throw new Error(`Document ID:${identity.id} (Type: ${identity.type}) not found.`);
            }
            const paragraphsResult = await this.textDocumentStorage.getParagraphs(identity);
            if (!paragraphsResult) {
                console.warn('[DocumentManager] Paragraphs not found for identity:', identity);
                throw new Error(`Paragraphs for Document ID:${identity.id} not found.`);
            }
            const paragraphData = paragraphsResult.map((p) => ({
                id: p.paragraphId,
                content: p.content,
                index: p.index,
            }));
            const loadResultToReturn = {
                identity: identity,
                paragraphs: paragraphData,
                rawParagraphs: paragraphsResult,
                language: document.language,
                targetLanguage: document.targetLanguage,
                title: document.title || '',
            };
            return loadResultToReturn;
        }
        catch (error) {
            console.error('[DocumentManager] Failed to load document from storage (identity:', identity, '):', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error(`Failed to load document for ${identity.id} (type ${identity.type}): ${String(error)}`);
        }
    }
    /**
     * 删除文档
     * 从存储中删除指定文档
     * @param identity 文档标识
     */
    async deleteDocument(identity) {
        try {
            await this.textDocumentStorage.deleteDocument(identity);
        }
        catch (error) {
            console.error(`Failed to delete document ID:${identity.id} (Type: ${identity.type}):`, error);
            throw error;
        }
    }
    /**
     * 更新阅读进度
     * @param identity 文档标识
     * @param position 最后阅读位置
     */
    async updateReadingPosition(identity, position) {
        try {
            await this.textDocumentStorage.updateDocument(identity, {
                readingAnchor: position,
                lastAccessTime: Date.now(),
            });
        }
        catch (error) {
            console.error('Failed to update reading position:', error);
            throw error;
        }
    }
    /**
     * 获取所有文档
     * @returns 文档列表
     */
    async getAllDocuments() {
        try {
            return await this.textDocumentStorage.getAllDocuments();
        }
        catch (error) {
            console.error('Failed to get all documents:', error);
            throw error;
        }
    }
}
DocumentManager.instance = null;
//# sourceMappingURL=DocumentManager.js.map